import kpLogo from '@/assets/kp-logo.png';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C1b §7] THE DASHBOARD WATERMARK — THE FORCE'S MARK, BEHIND THE TILES.
 *
 *  Bottom-RIGHT and never centred: tiles render over it, and a mark behind a number either
 *  vanishes or interferes with reading it. The gutter is where there is room for it.
 *
 *  ─── ⚠️ IT IS A MASK, NOT AN `<img>`, AND THAT IS WHAT MAKES §7a AND §7b ONE THING ──
 *
 *  §7a asks for the crest and the wordmark "both in one muted token"; §7b for "its own dark
 *  value, not the same opacity". An `<img>` cannot take a colour, so its per-theme treatment
 *  would have to be an OPACITY — which is not a colour and therefore cannot live in
 *  `tokens.ts`, the only file in this repository permitted to hold one (divergence #12). The
 *  remaining ways to vary it are a `dark:` utility, which §8 BANS in `components/` because it
 *  is a descendant match a nested `ThemeScope` cannot beat, or a second non-colour custom
 *  property, which widens `SemanticTokens` from a colour map into a mixed one.
 *
 *  `mask-image` sidesteps all of it: the PNG's ALPHA CHANNEL becomes the shape and
 *  `background-color` fills it, so the crest and the words are the SAME custom property and
 *  the per-theme difference is that token's own alpha. `ThemeScope` picks it up for free,
 *  which is the entire reason `dark:` is banned in the first place.
 *
 *  ⚠️ `maskRepeat` AND `maskSize` ARE NOT OPTIONAL. The initial value of `mask-repeat` is
 *  `repeat`, so without them the crest TILES across the corner — and jsdom resolves no
 *  cascade, so nothing in the unit suite can see that. `e2e/specs/dashboard.spec.ts` is what
 *  looks at it.
 *
 *  ⚠️ THE `-webkit-` PAIR IS STILL REQUIRED. Safari has not unprefixed `mask-image`.
 *
 *  ⚠️ §7e — IT IS THE SAME IMPORTED MODULE AS `AppSidebar.tsx` AND `pages/Login`. [P6d.1]
 *  records what a hand-copied crest costs: no diff, no lint rule and no test reads a binary,
 *  so a second copy stays the old mark for ever. There is no second copy to make.
 *
 *  ─── ⚠️ `pointer-events-none` IS LOAD-BEARING, AND `-z-10` DOES NOT REPLACE IT ──────
 *
 *  Negative z-index affects PAINTING, not hit testing. Under an opaque `bg-card` tile the
 *  mark is unreachable anyway; in the empty gutter — the only place it is visible — it is the
 *  topmost element under the cursor. §7c is exactly right, and `dashboard.spec.ts` asserts it
 *  with `document.elementFromPoint` rather than with "a click navigated", because the point
 *  is that the pointer passes through whatever happens to be underneath.
 *
 *  ⚠️ `aria-hidden`, AND IT ADDS NO ACCESSIBLE NAME. The wordmark is decoration; the app
 *  already names itself in the sidebar and the document title. A `banner`-adjacent element
 *  with a name is a second thing every `getByRole` sweep has to account for.
 *
 *  ⚠️ AND THERE IS NO `print:hidden`, DELIBERATELY. The printed order sheet is a route
 *  OUTSIDE the shell (`routes/index.tsx`), so this component is not on the page at all when
 *  anything is printed — a guard for an unreachable case is the dead tooling §9 removes. The
 *  claim is held at both poles by tests instead: `dashboard.spec.ts` asserts it is there,
 *  `print.spec.ts` asserts it is absent from the sheet under `emulateMedia({media:'print'})`.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
/**
 * ⚠️ THE FORCE, NOT THE APP. [P6d.1] renamed the dashboard to "PA Admin"; this is the crest
 * and the name of the organisation it belongs to, which is what §7a asks for.
 *
 * ⚠️ AND IT COLLIDES WITH A FIXTURE STRING. `test/renderWithProviders.tsx` gives its officer
 * an org unit of the same name, and `getByText` does not filter on `aria-hidden`. Nothing
 * queries that string today and the route gate keeps this off every screen but `/dashboard`,
 * so the two do not meet — but they are one `getByText('Kolkata Police')` away from meeting,
 * which is why it is said here rather than discovered in a rename.
 */
const WORDMARK = 'Kolkata Police';

export const AppWatermark = () => (
  <div
    aria-hidden
    data-testid="dashboard-watermark"
    /*
     * ⚠️ `@min-[1110px]/shell:` — THE SHELL ROW, NEVER THE WINDOW. The number is
     * `WATERMARK_THRESHOLD_PX` in `lib/layout.ts`, where it is DERIVED from the 1366 floor
     * minus the sidebar; `layout.test.ts` pins this literal against it, because Tailwind
     * extracts candidates from source TEXT and cannot read a constant.
     */
    className="pointer-events-none absolute bottom-6 right-6 -z-10 hidden select-none flex-col items-center gap-2 @min-[1110px]/shell:flex"
  >
    <span
      className="block size-24 bg-watermark"
      style={{
        maskImage: `url("${kpLogo}")`,
        WebkitMaskImage: `url("${kpLogo}")`,
        maskRepeat: 'no-repeat',
        WebkitMaskRepeat: 'no-repeat',
        maskSize: 'contain',
        WebkitMaskSize: 'contain',
        maskPosition: 'center',
        WebkitMaskPosition: 'center',
      }}
    />
    <span className="text-2xl font-semibold tracking-tight text-watermark">{WORDMARK}</span>
  </div>
);
